import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Briefcase, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function CasaMadreExternalPolicy() {
  const [policy, setPolicy] = useState(null);
  const [items, setItems] = useState([]);
  const [cmCats, setCmCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newAllowed, setNewAllowed] = useState("");

  const load = async () => {
    const [p, list, cats] = await Promise.all([
      api.get("/casa-madre/external-policy"),
      api.get("/external-products"),
      api.get("/casa-madre/catalog-categories"),
    ]);
    setPolicy(p.data.policy || {});
    setItems(list.data.items || []);
    setCmCats(cats.data.categories || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/casa-madre/external-policy", policy);
      toast.success("Policy aggiornata");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const approveOrReject = async (id, status) => {
    await api.put(`/external-products/${id}`, { approval_status: status });
    toast.success(status === "approved" ? "Approvato" : "Rifiutato");
    load();
  };

  const addAllowed = () => {
    if (!newAllowed.trim()) return;
    setPolicy({ ...policy, allowed_categories: [...(policy.allowed_categories || []), newAllowed.trim()] });
    setNewAllowed("");
  };
  const removeAllowed = (c) => setPolicy({ ...policy, allowed_categories: (policy.allowed_categories || []).filter((x) => x !== c) });

  if (!policy) return <AdminLayout><div className="p-8">Caricamento...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="cm-external-policy-page">
        <div className="overline text-brand mb-2">Casa Madre</div>
        <h1 className="font-heading font-bold text-4xl tracking-tight mb-2">Brand esterni dei Rivenditori</h1>
        <p className="text-sm text-muted-foreground mb-8 max-w-3xl">
          Decidi se permettere ai tuoi rivenditori di vendere prodotti complementari di altri brand (es. tappeti, illuminazione, decor). I prodotti in concorrenza diretta con il tuo catalogo sono bloccati automaticamente. Eventuali extra oltre la quota gratuita vengono fatturati al rivenditore.
        </p>

        <Tabs defaultValue="policy">
          <TabsList>
            <TabsTrigger value="policy" data-testid="tab-policy">Policy</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Prodotti esterni ({items.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="policy" className="space-y-6 max-w-3xl">
            <div className="flex items-center justify-between p-4 border border-border rounded-sm bg-white">
              <div>
                <div className="font-medium">Abilita brand esterni</div>
                <div className="text-xs text-muted-foreground">Permetti ai rivenditori di aggiungere prodotti non forniti dal Brand</div>
              </div>
              <Switch checked={!!policy.enabled} onCheckedChange={(v) => setPolicy({ ...policy, enabled: v })} data-testid="policy-enabled" />
            </div>

            <div className="p-4 border border-border rounded-sm bg-white">
              <div className="font-medium mb-2">Categorie del tuo catalogo (NON disponibili per esterno)</div>
              <div className="flex gap-1.5 flex-wrap">
                {cmCats.length === 0 ? <span className="text-xs text-muted-foreground">Nessuna categoria nel tuo catalogo</span> :
                  cmCats.map((c) => <span key={c} className="px-2 py-1 text-xs rounded-sm bg-red-50 text-red-800 border border-red-200">{c}</span>)}
              </div>
              <div className="text-xs text-muted-foreground mt-3">Concorrenza bloccata automaticamente.</div>
            </div>

            <div className="p-4 border border-border rounded-sm bg-white">
              <div className="font-medium mb-2">Categorie ammesse (whitelist opzionale)</div>
              <div className="flex gap-1.5 flex-wrap mb-2">
                {(policy.allowed_categories || []).map((c) => (
                  <span key={c} className="px-2 py-1 text-xs rounded-sm bg-emerald-50 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                    {c} <button onClick={() => removeAllowed(c)} className="text-emerald-900">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={newAllowed} onChange={(e) => setNewAllowed(e.target.value)} placeholder="es. tappeti, illuminazione, complementi"
                  className="flex-1 h-9 px-3 rounded-sm border border-border text-sm" data-testid="allowed-new-input" />
                <button onClick={addAllowed} className="h-9 px-3 rounded-sm border border-border text-sm hover:bg-accent">+ Aggiungi</button>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="overline text-muted-foreground mb-1">Inclusi gratuiti</div>
                <input type="number" value={policy.free_products_included || 0} onChange={(e) => setPolicy({ ...policy, free_products_included: parseInt(e.target.value) || 0 })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" data-testid="policy-free" />
              </div>
              <div className="p-4 border border-border rounded-sm bg-white">
                <div className="overline text-muted-foreground mb-1">€ / prodotto extra / mese</div>
                <input type="number" step="0.01" value={policy.price_per_extra_product_month || 0} onChange={(e) => setPolicy({ ...policy, price_per_extra_product_month: parseFloat(e.target.value) || 0 })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" data-testid="policy-fee" />
              </div>
              <div className="p-4 border border-border rounded-sm bg-white flex flex-col">
                <div className="overline text-muted-foreground mb-1">Approvazione necessaria</div>
                <Switch checked={!!policy.require_approval} onCheckedChange={(v) => setPolicy({ ...policy, require_approval: v })} data-testid="policy-approval" />
              </div>
            </div>

            <button onClick={save} disabled={busy} data-testid="save-policy-btn"
              className="h-11 px-6 rounded-sm bg-brand text-white text-sm disabled:opacity-50">
              {busy ? "Salvataggio..." : "Salva policy"}
            </button>
          </TabsContent>

          <TabsContent value="products" className="space-y-2">
            {items.length === 0 ? (
              <div className="text-center py-16 border border-dashed border-border rounded-sm">
                <Briefcase className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-muted-foreground">Nessun prodotto esterno inserito dai rivenditori</p>
              </div>
            ) : items.map((it) => (
              <div key={it.id} className="p-4 border border-border rounded-sm bg-white flex items-center gap-4" data-testid={`ext-row-${it.sku}`}>
                <div className="w-14 h-14 bg-neutral-100 rounded-sm overflow-hidden flex-shrink-0">
                  {it.immagini?.[0] && <img src={it.immagini[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.nome}</div>
                  <div className="text-xs text-muted-foreground">{it.brand} · {it.categoria} · SKU {it.sku} · da {it.tenant_name}</div>
                </div>
                <div className="text-sm font-medium">€ {it.prezzo}</div>
                <div className="flex items-center gap-1">
                  {it.approval_status === "pending" && <span className="px-2 py-0.5 text-xs rounded-sm bg-amber-100 text-amber-800 inline-flex items-center gap-1"><Clock className="w-3 h-3" />In attesa</span>}
                  {it.approval_status === "approved" && <span className="px-2 py-0.5 text-xs rounded-sm bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Approvato</span>}
                  {it.approval_status === "rejected" && <span className="px-2 py-0.5 text-xs rounded-sm bg-red-100 text-red-800 inline-flex items-center gap-1"><XCircle className="w-3 h-3" />Rifiutato</span>}
                </div>
                {it.approval_status !== "approved" && (
                  <button onClick={() => approveOrReject(it.id, "approved")} data-testid={`approve-${it.sku}`} className="h-8 px-3 rounded-sm border border-emerald-300 text-emerald-700 text-xs hover:bg-emerald-50">Approva</button>
                )}
                {it.approval_status !== "rejected" && (
                  <button onClick={() => approveOrReject(it.id, "rejected")} data-testid={`reject-${it.sku}`} className="h-8 px-3 rounded-sm border border-red-300 text-red-700 text-xs hover:bg-red-50">Rifiuta</button>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
}
